import { Clock } from 'lucide-react'
import { cn } from '../../lib/cn.js'

export interface TimePickerProps {
  /** `HH:MM`, 24-hour. What it is *stored* as; what it is read as is another thing. */
  value: string | null
  onChange: (value: string) => void
  /**
   * How far apart the choices step, in minutes.
   *
   * Guidance for the arrow keys and the browser's own picker, not a wall: a
   * time already on the record is shown whatever minute it falls on. This was
   * a `<select>` built from the step, and two things followed from that.
   *
   * Two hundred and eighty-eight options at a five-minute step, which is
   * what the class editor asks for — reaching seven in the evening meant
   * scrolling past two hundred and thirty rows, or typing "7" and landing on
   * seven in the morning, because a native select matches the label it draws.
   *
   * And a time **not on the grid** could not be represented at all. A class
   * starting at 19:07 — set from anywhere but this box — put a value in the
   * select that no option carried, so the field drew blank while the draft
   * still held the real time. What somebody saw and what they would save
   * were different, silently.
   */
  stepMinutes?: 5 | 10 | 15 | 30
  min?: string
  max?: string
  invalid?: boolean
  disabled?: boolean
  id?: string
  'aria-describedby'?: string
}

/**
 * A time of day, on its own.
 *
 * Its own component because 9.6a says the field is its own: folding it into
 * the date is the bug that screen already had. Stored as `HH:MM` and read in
 * whatever the reader's clock shows — two different questions, which an
 * earlier version of this answered with one string.
 *
 * `input type="time"` rather than a list. The list was chosen because
 * *"2:30pm", "14:30" and "2.30" are three things a parser has to be right
 * about* — which is true of a **text box**, and this is not one: the browser
 * does the parsing, in the reader's own format, and hands back `HH:MM`. It
 * also gives arrow keys, typing, and a value for every minute of the day.
 */
export function TimePicker({
  value,
  onChange,
  stepMinutes = 15,
  min,
  max,
  invalid,
  disabled,
  id,
  ...aria
}: TimePickerProps) {
  return (
    <div className="relative flex items-center">
      <Clock className="pointer-events-none absolute left-4 size-4 text-ink-muted" aria-hidden />
      <input
        type="time"
        id={id}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        {...aria}
        value={value ?? ''}
        /* Seconds, which is what the attribute counts. */
        step={stepMinutes * 60}
        {...(min ? { min } : {})}
        {...(max ? { max } : {})}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-10 w-full rounded-md border bg-surface pl-10 pr-4 text-body text-ink outline-none',
          'transition-colors focus-visible:border-coral-fill focus-visible:ring-2 focus-visible:ring-coral-fill/25',
          'disabled:bg-sunk disabled:text-ink-muted disabled:cursor-not-allowed',
          /* The browser's own clock button, which would sit under ours. */
          '[&::-webkit-calendar-picker-indicator]:ml-auto',
          invalid ? 'border-error' : 'border-hairline-strong',
        )}
      />
    </div>
  )
}
